import React, { useState, useEffect } from 'react';
import './Team.css';
import { useAuth } from '../../../AuthContext';
import { getBackendUrl } from '../../../api';
import Navbar1 from '../Navbar/Navbar1.jsx';
import Mainbar from '../Navbar/Mainbar.jsx';

const CompleteTeamManagement = () => {
  const { user, isManager, canApproveLeaves } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [userProfilePictures, setUserProfilePictures] = useState({});
  
  const DEFAULT_LIMITS = {
    CASUAL: 15,
    MEDICAL: 15,
    PERMISSION: 2
  };

  const [leaveLimits, setLeaveLimits] = useState(DEFAULT_LIMITS);
  
  const [analytics, setAnalytics] = useState({
    availableCount: 0,
    onLeaveCount: 0,
    totalMembers: 0,
    avgLeavesPerPerson: 0,
    availablePercentage: 0,
    onLeavePercentage: 0
  });

  const getUserProfilePicture = async (employeeEmail, employeeName) => {
    if (!employeeEmail && !employeeName) {
      return 'https://via.placeholder.com/50?text=?';
    }
    
    const cacheKey = employeeEmail || employeeName;
    if (userProfilePictures[cacheKey]) {
      return userProfilePictures[cacheKey];
    }
    
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/employee-list`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });
      
      if (response.ok) {
        const employees = await response.json();
        const employee = employees.find(emp => 
          emp.email === employeeEmail || 
          emp.name === employeeName
        );
        
        if (employee && employee.profile_picture) {
          setUserProfilePictures(prev => ({
            ...prev,
            [cacheKey]: employee.profile_picture
          }));
          return employee.profile_picture;
        }
      }
    } catch (error) {
      console.warn('Could not fetch user profile picture:', error);
    }
    
    const identifier = employeeEmail || employeeName || 'user';
    const fallbackUrl = `https://i.pravatar.cc/50?u=${encodeURIComponent(identifier)}`;
    
    setUserProfilePictures(prev => ({
      ...prev,
      [cacheKey]: fallbackUrl
    }));
    
    return fallbackUrl;
  };

  const getUserInitials = (name, email) => {
    if (name && name.trim()) {
      const names = name.trim().split(' ');
      if (names.length > 1) {
        return (names[0][0] + names[names.length - 1][0]).toUpperCase();
      }
      return name.trim().substring(0, 2).toUpperCase();
    }
    if (email) {
      return email.substring(0, 2).toUpperCase();
    }
    return 'TM';
  };

  const fetchLeavePolicies = async () => {
    try {
      const backendUrl = getBackendUrl();
      console.log('📋 [TEAM] Fetching leave policies...');
      
      const response = await fetch(`${backendUrl}/api/leave-policies`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('📋 [TEAM] Policies received:', data);
        
        if (data.leave_policies && Array.isArray(data.leave_policies)) {
          const newLimits = { ...DEFAULT_LIMITS };
          
          data.leave_policies.forEach(policy => {
            const leaveType = (policy.leave_type || '').toLowerCase();
            
            if (leaveType.includes('casual')) {
              newLimits.CASUAL = policy.max_days_per_year || DEFAULT_LIMITS.CASUAL;
            } else if (leaveType.includes('sick') || leaveType.includes('medical')) {
              newLimits.MEDICAL = policy.max_days_per_year || DEFAULT_LIMITS.MEDICAL;
            } else if (leaveType.includes('emergency') || leaveType.includes('permission')) {
              newLimits.PERMISSION = policy.max_days_per_year || DEFAULT_LIMITS.PERMISSION;
            }
          });
          
          console.log('✅ [TEAM] Updated limits:', newLimits);
          setLeaveLimits(newLimits);
          return newLimits;
        }
      } else {
        console.warn('⚠️ [TEAM] Failed to fetch policies:', response.status);
      }
      
      return DEFAULT_LIMITS;
    } catch (error) {
      console.error('❌ [TEAM] Error loading policies:', error);
      return DEFAULT_LIMITS;
    }
  };

  const getMemberLeaveSummary = async (memberId, currentLimits) => {
    try {
      const backendUrl = getBackendUrl();
      const summaryUrl = `${backendUrl}/api/manager/team-member-summary/${memberId}`;
      
      console.log(`🔍 [TEAM] Fetching summary for member ${memberId}`);
      
      const response = await fetch(summaryUrl, {
        method: 'GET',
        credentials: 'include',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log(`✅ [TEAM] Summary received for member ${memberId}:`, data);
        
        const casualUsed = Number(data.casual) || 0;
        const casualPending = Number(data.casual_pending) || 0;
        const medicalUsed = Number(data.medical) || 0;
        const medicalPending = Number(data.medical_pending) || 0;
        const permissionUsed = Number(data.permission) || 0;
        const permissionPending = Number(data.permission_pending) || 0;
        
        return {
          casual: casualUsed,
          casualPending,
          casualTotal: currentLimits.CASUAL,
          casualAvailable: Math.max(0, currentLimits.CASUAL - casualUsed - casualPending),
          
          medical: medicalUsed,
          medicalPending,
          medicalTotal: currentLimits.MEDICAL,
          medicalAvailable: Math.max(0, currentLimits.MEDICAL - medicalUsed - medicalPending),
          
          permission: permissionUsed,
          permissionPending,
          permissionTotal: currentLimits.PERMISSION,
          permissionAvailable: Math.max(0, currentLimits.PERMISSION - permissionUsed - permissionPending)
        };
      } else {
        console.warn(`⚠️ [TEAM] Failed to fetch summary for member ${memberId}`);
      }
      
      return {
        casual: 0,
        casualPending: 0,
        casualTotal: currentLimits.CASUAL,
        casualAvailable: currentLimits.CASUAL,
        
        medical: 0,
        medicalPending: 0,
        medicalTotal: currentLimits.MEDICAL,
        medicalAvailable: currentLimits.MEDICAL,
        
        permission: 0,
        permissionPending: 0,
        permissionTotal: currentLimits.PERMISSION,
        permissionAvailable: currentLimits.PERMISSION
      };
    } catch (error) {
      console.error(`❌ [TEAM] Error fetching summary for member ${memberId}:`, error);
      return {
        casual: 0,
        casualPending: 0,
        casualTotal: currentLimits.CASUAL,
        casualAvailable: currentLimits.CASUAL,
        
        medical: 0,
        medicalPending: 0,
        medicalTotal: currentLimits.MEDICAL,
        medicalAvailable: currentLimits.MEDICAL,
        
        permission: 0,
        permissionPending: 0,
        permissionTotal: currentLimits.PERMISSION,
        permissionAvailable: currentLimits.PERMISSION
      };
    }
  };

  const determinePerformance = (leaveSummary) => {
    const totalUsed = (leaveSummary.casual || 0) + (leaveSummary.medical || 0);
    
    if (totalUsed <= 3) {
      return 'Excellent';
    } else if (totalUsed <= 10) {
      return 'Average';
    } else {
      return 'Normal';
    }
  };

  const fetchTeamData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const backendUrl = getBackendUrl();
      
      console.log('\n🔥 ============ FETCHING TEAM DATA ============');
      console.log('Backend URL:', backendUrl);
      console.log('Current user:', user?.email);
      
      console.log('\n📋 STEP 1: Fetching leave policies...');
      const currentLimits = await fetchLeavePolicies();
      console.log('✅ Leave limits loaded:', currentLimits);
      
      console.log('\n👥 STEP 2: Fetching team members...');
      const response = await fetch(`${backendUrl}/api/manager/team-members`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      console.log('Team members API response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch team members: ${response.status} - ${errorText}`);
      }

      const teamData = await response.json();
      console.log('Raw team data received:', teamData);
      
      const teamMembers = teamData.team_members || [];
      console.log(`✅ Found ${teamMembers.length} team members`);

      if (teamMembers.length === 0) {
        console.log('⚠️ No team members found');
        setTeamMembers([]);
        setLoading(false);
        return;
      }

      console.log('\n📊 STEP 3: Processing team members...');
      
      const processedMembers = await Promise.all(
        teamMembers.map(async (member, index) => {
          try {
            console.log(`\n🔍 [${index + 1}/${teamMembers.length}] Processing: ${member.name} (ID: ${member.id})`);
            
            const profilePicture = await getUserProfilePicture(member.email, member.name);
            console.log(`   Profile picture URL: ${profilePicture}`);
            
            const leaveSummary = await getMemberLeaveSummary(member.id, currentLimits);
            
            console.log(`📊 Summary for ${member.name}:`, {
              casual: `${leaveSummary.casual}/${leaveSummary.casualTotal}`,
              medical: `${leaveSummary.medical}/${leaveSummary.medicalTotal}`,
              permission: `${leaveSummary.permission}/${leaveSummary.permissionTotal}`
            });
            
            const isOnLeaveToday = member.on_leave_today === true;
            const status = isOnLeaveToday ? 'On Leave' : 'Available';
            
            console.log(`   Status: ${status} (on_leave_today: ${member.on_leave_today})`);
            
            const performance = determinePerformance(leaveSummary);
            console.log(`   Performance: ${performance}`);
            
            const totalLeaves = leaveSummary.casual + leaveSummary.medical;
            const averagePerMonth = (totalLeaves / 12).toFixed(1);
            const normalTotal = leaveSummary.casualTotal + leaveSummary.medicalTotal;
            
            const processedMember = {
              id: member.id,
              name: member.name || 'Unknown',
              email: member.email || '',
              profilePicture: profilePicture,
              status: status,
              
              casualUsed: leaveSummary.casual,
              casualPending: leaveSummary.casualPending,
              casualTotal: leaveSummary.casualTotal,
              casualAvailable: leaveSummary.casualAvailable,
              
              medicalUsed: leaveSummary.medical,
              medicalPending: leaveSummary.medicalPending,
              medicalTotal: leaveSummary.medicalTotal,
              medicalAvailable: leaveSummary.medicalAvailable,
              
              permissionUsed: leaveSummary.permission,
              permissionPending: leaveSummary.permissionPending,
              permissionTotal: leaveSummary.permissionTotal,
              permissionAvailable: leaveSummary.permissionAvailable,
              
              performance: performance,
              averagePerMonth: averagePerMonth,
              normalTotal: normalTotal,
              
              role: member.role || 'Employee',
              department: member.department || 'Engineering',
              onLeaveToday: isOnLeaveToday
            };
            
            console.log(`✅ [${index + 1}/${teamMembers.length}] Processed ${member.name} with profile picture`);
            
            return processedMember;
          } catch (memberError) {
            console.error(`❌ Error processing member ${member.id} (${member.name}):`, memberError);
            
            const fallbackPicture = await getUserProfilePicture(member.email, member.name).catch(() => 
              `https://i.pravatar.cc/50?u=${encodeURIComponent(member.email || member.name)}`
            );
            
            return {
              id: member.id,
              name: member.name || 'Unknown',
              email: member.email || '',
              profilePicture: fallbackPicture,
              status: 'Available',
              casualUsed: 0,
              casualTotal: currentLimits.CASUAL,
              medicalUsed: 0,
              medicalTotal: currentLimits.MEDICAL,
              permissionUsed: 0,
              permissionTotal: currentLimits.PERMISSION,
              performance: 'Normal',
              averagePerMonth: '0.0',
              normalTotal: currentLimits.CASUAL + currentLimits.MEDICAL,
              role: member.role || 'Employee',
              department: member.department || 'Engineering',
              onLeaveToday: false
            };
          }
        })
      );

      const validMembers = processedMembers.filter(m => m !== null);
      
      console.log('\n🎉 ============ PROCESSING COMPLETE ============');
      console.log(`Successfully processed ${validMembers.length} team members`);
      console.log('Processed members with profile pictures:', validMembers.map(m => ({
        name: m.name,
        email: m.email,
        profilePicture: m.profilePicture,
        status: m.status
      })));
      
      setTeamMembers(validMembers);
      setError(null);
      
    } catch (err) {
      console.error('❌ CRITICAL ERROR fetching team data:', err);
      setError(err.message || 'Failed to load team data');
      setTeamMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateAnalytics = () => {
    try {
      if (!teamMembers || teamMembers.length === 0) {
        console.log('⚠️ [ANALYTICS] No team members to analyze');
        setAnalytics({
          availableCount: 0,
          onLeaveCount: 0,
          totalMembers: 0,
          avgLeavesPerPerson: 0,
          availablePercentage: 0,
          onLeavePercentage: 0
        });
        return;
      }

      console.log('\n📊 ============ CALCULATING ANALYTICS ============');
      console.log(`Total team members: ${teamMembers.length}`);

      const onLeaveMembers = teamMembers.filter(m => m.onLeaveToday === true);
      const availableMembers = teamMembers.filter(m => m.onLeaveToday !== true);
      
      const onLeaveCount = onLeaveMembers.length;
      const availableCount = availableMembers.length;
      const totalCount = teamMembers.length;

      console.log(`Available: ${availableCount} members`);
      console.log(`On Leave: ${onLeaveCount} members`);
      console.log('On leave members:', onLeaveMembers.map(m => m.name).join(', ') || 'None');

      const availablePercentage = totalCount > 0 ? Math.round((availableCount / totalCount) * 100) : 0;
      const onLeavePercentage = totalCount > 0 ? Math.round((onLeaveCount / totalCount) * 100) : 0;

      console.log(`Available percentage: ${availablePercentage}%`);
      console.log(`On leave percentage: ${onLeavePercentage}%`);

      const totalLeavesUsed = teamMembers.reduce(
        (sum, member) => sum + (member.casualUsed + member.medicalUsed),
        0
      );

      const avgLeavesPerYear = totalLeavesUsed / (teamMembers.length || 1);
      const avgLeavesPerMonth = avgLeavesPerYear / 12;

      console.log(`Total leaves used (year): ${totalLeavesUsed}`);
      console.log(`Average leaves per person per month: ${avgLeavesPerMonth.toFixed(2)}`);

      const calculatedAnalytics = {
        availableCount,
        onLeaveCount,
        totalMembers: totalCount,
        avgLeavesPerPerson: Number(avgLeavesPerMonth.toFixed(2)),
        availablePercentage,
        onLeavePercentage
      };

      console.log('✅ [ANALYTICS] Final analytics:', calculatedAnalytics);
      
      setAnalytics(calculatedAnalytics);
    } catch (err) {
      console.error('❌ [ANALYTICS] Error calculating analytics:', err);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      if (!user) {
        console.log('❌ No user logged in');
        setError("Please log in to view team management");
        setLoading(false);
        return;
      }
      
      // Call isManager function to get boolean value
      const isUserManager = isManager();
      
      if (!isUserManager && !canApproveLeaves) {
        console.log('❌ Access denied - not a manager');
        setError("Access denied. Manager privileges required.");
        setLoading(false);
        return;
      }
      
      console.log('✅ User authorized, fetching team data...');
      await fetchTeamData();
    };

    initializeData();
  }, [user, canApproveLeaves]);

  useEffect(() => {
    if (teamMembers && teamMembers.length > 0) {
      console.log('📊 Calculating analytics for', teamMembers.length, 'members');
      calculateAnalytics();
    }
  }, [teamMembers]);

  const handleRefresh = async () => {
    console.log('🔄 Manual refresh triggered');
    // Reset state to show loading
    setLoading(true);
    setError(null);
    // Clear cached profile pictures to force refresh
    setUserProfilePictures({});
    try {
      await fetchTeamData();
    } catch (err) {
      console.error('❌ Error during refresh:', err);
      setError(err.message || 'Failed to refresh team data');
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const filteredTeamMembers = teamMembers.filter(member => {
    const query = searchQuery.toLowerCase();
    return (
      member.name.toLowerCase().includes(query) ||
      member.email.toLowerCase().includes(query) ||
      member.role.toLowerCase().includes(query) ||
      member.department.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="team-container">
        <div className="manager-navbar">
          <Navbar1 />
        </div>
        <div className="team-main-content" style={{marginLeft:'127px'}}>
          <div className="team-topbar">
            <Mainbar />
          </div>
          <div className="team-page-wrapper">
            <div className="loading-state">
              <div className="loading-spinner" style={{
                width: '40px',
                height: '40px',
                border: '4px solid #f3f4f6',
                borderTop: '4px solid #2196F3',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}>
                <style>
                  {`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}
                </style>
              </div>
              {/* <p style={{marginTop: '10px'}}>Loading team data...</p> */}
              {/* Fallback text animation for older browsers */}
              {/* <div style={{
                fontSize: '24px', 
                marginTop: '10px',
                animation: 'spin 1s linear infinite'
              }}>↻
                <style>
                  {`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}
                </style>
              </div> */}
              {/* Simple text fallback */}
              {/* <div style={{marginTop: '10px', fontSize: '14px', color: '#666'}}>
                Refreshing data...
              </div> */}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="team-container">
        <div className="manager-navbar">
          <Navbar1 />
        </div>
        <div className="team-main-content">
          <div className="team-topbar">
            <Mainbar />
          </div>
          <div className="team-page-wrapper">
            <div className="error-state">
              <p className="error-message">{error}</p>
              <button onClick={handleRefresh} className="retry-button">
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="team-container">
      <div className="manager-navbar">
        <Navbar1 />
      </div>
      
      <div className="team-main-content">
        <div className="team-topbar">
          <Mainbar />
        </div>
        
        <div className="team-page-wrapper">
          {/* Header Section */}
          <div className="team-header">
            <div className="team-header-left">
              <h1 className="team-title">Team Management</h1>
              <p className="team-subtitle">
                Manage your team of {teamMembers.length} member{teamMembers.length !== 1 ? 's' : ''}
              </p>
            </div>
            
            <div className="team-header-right">
              <div className="search-box-manag-team">
                <svg className="search-icon-manag-team" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path 
                    d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" 
                    stroke="currentColor" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                  <path 
                    d="M14 14L11.1 11.1" 
                    stroke="currentColor" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
                <input 
                  type="text" 
                  placeholder="Search for..." 
                  value={searchQuery}
                  onChange={handleSearch}
                  className="search-input"
                />
              </div>
              
              <button className="refresh-btn" onClick={handleRefresh}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path 
                    d="M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2" 
                    stroke="currentColor" 
                    strokeWidth="1.5" 
                    strokeLinecap="round"
                  />
                  <path 
                    d="M8 2L10 4L8 6" 
                    stroke="currentColor" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
                Refresh
              </button>
            </div>
          </div>

          {/* Team Members List */}
          <div className="team-members-list">
            {filteredTeamMembers.length > 0 ? (
              filteredTeamMembers.map((member) => (
                <div key={member.id} className="team-member-card">
                  {/* Left Section: Avatar and Details */}
                  <div className="member-left">
                    <div className={`member-avatar ${member.onLeaveToday ? 'on-leave' : ''}`}>
                      <img 
                        src={member.profilePicture}
                        alt={member.name}
                        className="profile-image"
                        onError={(e) => {
                          e.target.src = `https://i.pravatar.cc/50?u=${encodeURIComponent(member.email)}`;
                        }}
                      />
                    </div>
                    <div className="member-details">
                      <h3 className="member-name">{member.name}</h3>
                      <p className="member-email">{member.email}</p>
                      <p className="member-role">
                        {member.role} · {member.department}
                      </p>
                    </div>
                  </div>
                  
                  {/* Center Section: Performance - FIXED: Removed Button wrappers */}
                  <div className="member-center">
                    <div className="performance-indicators">
                      <div className="indicator-group">
                        <div className={`indicator ${member.performance === 'Excellent' ? 'active' : ''}`}>
                          Excellent
                        </div>
                        <div className={`indicator ${member.performance === 'Average' ? 'active' : ''}`}>
                          Average
                        </div>
                        <div className={`indicator ${member.performance === 'Normal' ? 'active' : ''}`}>
                          Normal
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Right Section: Status Badge */}
                  <div className="member-right">
                    <span className={`status-badge ${member.status.toLowerCase().replace(' ', '-')}`}>
                      {member.status}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-members">
                <p>
                  {searchQuery 
                    ? `No members found matching "${searchQuery}"` 
                    : 'No team members found'}
                </p>
                {!searchQuery && (
                  <button onClick={handleRefresh} className="retry-button">
                    Refresh Data
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Team Analytics Section */}
          <div className="team-analytics-section">
            <h2 className="analytics-title">Team Analytics</h2>
            
            <div className="analytics-grid">
              {/* Available Today Card */}
              <div className="analytics-card blue">
                <div className="analytics-label">Team Available Today</div>
                <div className="analytics-value">{analytics.availablePercentage}%</div>
                <div className="analytics-progress">
                  <div 
                    className="analytics-progress-bar" 
                    style={{ width: `${analytics.availablePercentage}%` }}
                  />
                </div>
              </div>
              
              {/* On Leave Today Card */}
              <div className="analytics-card red">
                <div className="analytics-label">Team On Leave Today</div>
                <div className="analytics-value">
                  {analytics.onLeaveCount}/{analytics.totalMembers}
                </div>
                <div className="analytics-progress">
                  <div 
                    className="analytics-progress-bar" 
                    style={{ width: `${analytics.onLeavePercentage}%` }}
                  />
                </div>
              </div>
              
              {/* Average Leaves Card */}
              <div className="analytics-card yellow">
                <div className="analytics-label">Avg Approved Leaves (This Month)</div>
                <div className="analytics-value">{analytics.avgLeavesPerPerson}</div>
                <div className="analytics-progress">
                  <div 
                    className="analytics-progress-bar" 
                    style={{ width: `${Math.min(analytics.avgLeavesPerPerson * 20, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompleteTeamManagement;